import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, ErrorState, Toggle } from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { useSession } from '@shared/lib/auth/session-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { toast } from '@shared/lib/toast/toast-store'
import { getEmailPreferences, updateEmailPreferences } from './email-preferences.api'
import type { EmailPreferences } from './email-preferences.api'
import * as styles from './EmailNotificationPreferences.css'

const options: ReadonlyArray<readonly [keyof EmailPreferences, string, string]> = [
  ['salePaid', 'Vendas aprovadas', 'Quando uma venda em que você participa é paga.'],
  [
    'subscriptionCreated',
    'Novas assinaturas',
    'Quando uma assinatura de um dos seus produtos é iniciada, inclusive em período de teste.',
  ],
  [
    'subscriptionCanceled',
    'Assinaturas canceladas',
    'Quando uma assinatura de um dos seus produtos é cancelada.',
  ],
  [
    'subscriptionPaymentFailed',
    'Problemas na cobrança de assinaturas',
    'Quando o pagamento de uma assinatura é recusado ou precisa de confirmação do comprador.',
  ],
  [
    'payoutPaid',
    'Transferências para sua conta',
    'Quando um repasse automático para sua conta bancária é concluído.',
  ],
]

export function EmailNotificationPreferences() {
  const clarify = useFeatureFlag('emailNotificationClarity')
  const userId = useSession((state) => state.user?.id)
  const queryClient = useQueryClient()
  const queryKey = ['email-preferences', userId]
  const query = useQuery({ queryKey, queryFn: getEmailPreferences, enabled: Boolean(userId) })
  const mutation = useMutation({ mutationFn: updateEmailPreferences })
  const [draft, setDraft] = useState<Partial<EmailPreferences>>({})
  const changes: Partial<EmailPreferences> = {}
  for (const [key] of options) {
    if (draft[key] !== undefined && draft[key] !== query.data?.[key]) changes[key] = draft[key]
  }

  async function save() {
    if (!query.data || Object.keys(changes).length === 0 || mutation.isPending) return
    try {
      const saved = await mutation.mutateAsync(changes)
      await queryClient.cancelQueries({ queryKey, exact: true })
      queryClient.setQueryData(queryKey, saved)
      setDraft({})
      toast.success({
        title: 'Preferências salvas',
        description: 'Suas preferências de email foram atualizadas.',
      })
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível salvar as preferências',
          fallback: 'Suas alterações continuam nesta tela. Tente salvar novamente.',
        }),
      )
    }
  }

  return (
    <Card data-testid="email-notifications-screen">
      <div className={styles.content}>
        <h2 className={styles.heading}>Preferências de email</h2>
        <p className={styles.copy}>
          Emails de segurança e comunicações essenciais continuam ativos. Esta escolha não afeta
          emails dos compradores, notificações push ou webhooks.
        </p>
        {query.isError ? (
          <ErrorState
            title="Não foi possível carregar as preferências"
            description="Tente novamente para consultar e alterar seus emails."
            onRetry={() => {
              void query.refetch()
            }}
          />
        ) : !query.data ? (
          <p className={styles.copy} role="status">
            Carregando preferências...
          </p>
        ) : (
          <form
            className={styles.content}
            data-testid="email-notifications-form"
            onSubmit={(event) => {
              event.preventDefault()
              void save()
            }}
          >
            <div>
              {options.map(([key, title, description]) => {
                const label = clarify
                  ? title
                  : key === 'payoutPaid'
                    ? 'Recebimentos concluídos'
                    : key === 'subscriptionPaymentFailed'
                      ? 'Problemas de cobrança de assinaturas'
                      : title
                return (
                  <div
                    key={key}
                    className={styles.row}
                    data-testid={`email-notifications-option-${key}`}
                  >
                    <div className={styles.optionCopy}>
                      <label htmlFor={`email-notifications-${key}`}>{label}</label>
                      {clarify && (
                        <p
                          id={`email-notifications-${key}-description`}
                          className={styles.description}
                        >
                          {description}
                        </p>
                      )}
                    </div>
                    <Toggle
                      id={`email-notifications-${key}`}
                      data-testid={`email-notifications-${key}`}
                      ariaLabel={label}
                      aria-describedby={
                        clarify ? `email-notifications-${key}-description` : undefined
                      }
                      checked={draft[key] ?? query.data[key]}
                      disabled={mutation.isPending}
                      onChange={(checked) => {
                        setDraft((current) => ({ ...current, [key]: checked }))
                      }}
                    />
                  </div>
                )
              })}
            </div>
            <div className={styles.actions}>
              <Button
                type="submit"
                data-testid="email-notifications-save-btn"
                disabled={mutation.isPending || Object.keys(changes).length === 0}
              >
                {mutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  )
}
